/**
 * DUA Playground Component
 *
 * An interactive development tool for testing and previewing Data Usage Agreement
 * (DUA) forms and diffs. Allows developers to:
 * - Edit JSON schemas and form data in real-time
 * - Toggle between form view and diff view modes
 * - Test context-based field visibility
 * - Preview read-only and text-only rendering modes
 *
 * All state is persisted in the URL hash for easy sharing and bookmarking.
 */
import {
  CodeEditor,
  ColumnLayout,
  Container,
  FormField,
  Multiselect,
  SegmentedControl,
  SpaceBetween,
  Toggle,
} from '@amzn/awsui-components-react-v3';
import React, { useState } from 'react';
import { UsageAgreementForm } from 'src/components/datausageagreement/usageAgreementForm';
import { UsageAgreementDiff } from 'src/components/datausageagreement/usageAgreementDiff';
import ace from 'ace-builds';
import { useUrlState } from 'src/commons/hooks/useUrlState';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import { DEFAULT_AFTER_DATA, DEFAULT_BEFORE_DATA, DEFAULT_DUA_SCHEMA } from 'src/components/tools/constants';

function DuaPlayground() {
  const [preferences, setPreferences] = useState(undefined);
  const [editorHeight, setEditorHeight] = useState(800);

  // URL-persisted state for all playground settings
  const [mode, setMode] = useUrlState('mode', 'form');
  const [contextsStr, setContextsStr] = useUrlState('contexts', 'default');
  const [schema, setSchema] = useUrlState('schema', DEFAULT_DUA_SCHEMA);
  const [data, setData] = useUrlState('data', DEFAULT_BEFORE_DATA);
  const [afterData, setAfterData] = useUrlState('afterData', DEFAULT_AFTER_DATA);

  // Boolean flags stored as strings in URL (empty string = false)
  const [readOnlyStr, setReadOnlyStr] = useUrlState('readOnly', '');
  const [textOnlyStr, setTextOnlyStr] = useUrlState('textOnly', '');
  const [showChangesOnlyStr, setShowChangesOnlyStr] = useUrlState('showChangesOnly', '');
  const readOnly = readOnlyStr === 'true';
  const textOnly = textOnlyStr === 'true';
  const showChangesOnly = showChangesOnlyStr === 'true';
  const setReadOnly = (value) => setReadOnlyStr(value ? 'true' : '');
  const setTextOnly = (value) => setTextOnlyStr(value ? 'true' : '');
  const setShowChangesOnly = (value) => setShowChangesOnlyStr(value ? 'true' : '');

  const [errors, setErrors] = useState([]);

  /** Safely parses JSON, returning empty object on failure */
  const safeParse = (data) => {
    try {
      return JSON.parse(data);
    } catch (e) {
      return {};
    }
  };

  /** Formats JSON string with indentation for display */
  const prettyfy = (data) => {
    try {
      return JSON.stringify(JSON.parse(data), null, 2);
    } catch (e) {
      return data;
    }
  };

  const selectedOptions: OptionDefinition[] = contextsStr
    ? contextsStr.split(',').map((v) => ({ label: v, value: v }))
    : [];
  const setSelectedOptions = (opts: OptionDefinition[]) => {
    setContextsStr(opts.map((o) => o.value).join(','));
  };

  const contexts = selectedOptions.map((v) => v.value);
  const isDiffMode = mode === 'diff';
  // In diff mode, split the data editor height to show before/after
  const dataEditorHeight = isDiffMode ? editorHeight / 2 - 60 : editorHeight;
  const schemaEditorHeight = editorHeight;

  // Shared props for all CodeEditor instances
  const editorProps = {
    language: '"json"',
    preferences,
    onPreferencesChange: (e) => setPreferences(e.detail),
    themes: { light: ['cloud_editor'], dark: ['cloud_editor_dark'] },
    ace,
  };

  return (
    <SpaceBetween size='m'>
      {/* Mode toggle: Form view vs Diff view */}
      <SegmentedControl
        selectedId={mode}
        onChange={({ detail }) => setMode(detail.selectedId)}
        options={[
          { id: 'form', text: 'Form' },
          { id: 'diff', text: 'Diff' },
        ]}
      />
      <ColumnLayout columns={2}>
        <Container header='Schema'>
          <CodeEditor
            {...editorProps}
            value={prettyfy(schema)}
            onDelayedChange={(event) => setSchema(event.detail.value)}
            editorContentHeight={schemaEditorHeight}
            onEditorContentResize={(e) => setEditorHeight(e.detail.height)}
          />
        </Container>
        {isDiffMode ? (
          <SpaceBetween size='m'>
            <Container header='Before'>
              <CodeEditor
                {...editorProps}
                value={prettyfy(data)}
                onDelayedChange={(event) => setData(event.detail.value)}
                editorContentHeight={dataEditorHeight}
              />
            </Container>
            <Container header='After'>
              <CodeEditor
                {...editorProps}
                value={prettyfy(afterData)}
                onDelayedChange={(event) => setAfterData(event.detail.value)}
                editorContentHeight={dataEditorHeight}
              />
            </Container>
          </SpaceBetween>
        ) : (
          <Container header='Data'>
            <CodeEditor
              {...editorProps}
              value={prettyfy(data)}
              onDelayedChange={(event) => setData(event.detail.value)}
              editorContentHeight={dataEditorHeight}
            />
          </Container>
        )}
      </ColumnLayout>
      <Container header='Properties'>
        <SpaceBetween size='m' direction='horizontal'>
          <SpaceBetween size='m' direction='vertical'>
            <Toggle onChange={({ detail }) => setReadOnly(detail.checked)} checked={readOnly} disabled={isDiffMode}>
              Read only
            </Toggle>
            <Toggle onChange={({ detail }) => setTextOnly(detail.checked)} checked={textOnly} disabled={isDiffMode}>
              Text only
            </Toggle>
            <Toggle
              onChange={({ detail }) => setShowChangesOnly(detail.checked)}
              checked={showChangesOnly}
              disabled={!isDiffMode}
            >
              Show changes only
            </Toggle>
          </SpaceBetween>
          <FormField label='Contexts' description={'Display the fields for the selected contexts'}>
            <Multiselect
              selectedOptions={selectedOptions}
              onChange={({ detail }) => setSelectedOptions(detail.selectedOptions)}
              options={[
                { label: 'default', value: 'default' },
                { label: 'requests', value: 'requests' },
                { label: 'reviews', value: 'reviews' },
              ]}
              placeholder='Choose options'
            />
          </FormField>
        </SpaceBetween>
      </Container>
      <Container header='Preview'>
        {isDiffMode ? (
          <UsageAgreementDiff
            schema={schema}
            contexts={contexts}
            beforeFormData={safeParse(data)}
            afterFormData={safeParse(afterData)}
            errors={errors}
            setErrors={setErrors}
            showChangesOnly={showChangesOnly}
          />
        ) : (
          <UsageAgreementForm
            // Key forces re-mount when display modes change to reset form state
            key={`${readOnly}-${textOnly}`}
            schema={schema}
            contexts={contexts}
            formData={safeParse(data)}
            setFormData={(data) => setData(JSON.stringify(data))}
            errors={errors}
            setErrors={setErrors}
            readOnly={readOnly}
            textOnly={textOnly}
          />
        )}
      </Container>
    </SpaceBetween>
  );
}

export default DuaPlayground;
